import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';

/**
 * Hook to fetch paginated list of workflows
 */
export function useWorkflows(params?: {
  page?: number;
  pageSize?: number;
  projectId?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: queryKeys.workflowList(params),
    queryFn: () => api.getWorkflows(params),
  });
}

/**
 * Hook to fetch a single workflow by ID
 */
export function useWorkflow(id: string) {
  return useQuery({
    queryKey: queryKeys.workflowDetail(id),
    queryFn: () => api.getWorkflow(id),
    enabled: Boolean(id),
    // Refetch more frequently for running workflows
    refetchInterval: (query) => {
      const workflow = query.state.data;
      if (workflow?.status === 'running' || workflow?.status === 'pending') {
        return 5000; // Refetch every 5 seconds
      }
      return false;
    },
  });
}

/**
 * Hook to trigger a new workflow
 */
export function useTriggerWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      workflowName,
    }: {
      projectId: string;
      workflowName: string;
    }) => api.triggerWorkflow(projectId, workflowName),
    onSuccess: (_, variables) => {
      // Invalidate workflows lists
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectWorkflows(variables.projectId),
      });
      // Invalidate dashboard stats
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

/**
 * Hook to cancel a running workflow
 */
export function useCancelWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.cancelWorkflow(id),
    onSuccess: (cancelledWorkflow) => {
      // Update the specific workflow in cache
      queryClient.setQueryData(
        queryKeys.workflowDetail(cancelledWorkflow.id),
        cancelledWorkflow
      );
      // Invalidate workflows list
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows });
      // Invalidate dashboard stats
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}
